/**
 * pdfIncludeState — gère les flags "inclure dans le PDF" pour chaque
 * section du document.
 *
 * 3 domaines :
 *
 *   1. **Fiche sous-sections** (bien / reglement / risques / dvf) —
 *      stockées sur le doc fiche racine dans un nouveau champ
 *      `pdfIncludeFiche`. C'est une addition au type `FicheDoc` ; l'absence
 *      du champ = tout inclus (default true) pour compatibilité avec les
 *      fiches historiques.
 *
 *   2. **Env blocks** — flag `pdfInclude` déjà présent sur chaque doc
 *      `/users/{uid}/fiches/{fid}/env/{blockId}`. Lu/écrit directement.
 *
 *   3. **AI blocks** — flag `pdfInclude` déjà présent sur chaque doc
 *      `/users/{uid}/fiches/{fid}/ai/{blockId}`. Lu/écrit directement.
 *
 * La modale lit cet état à l'ouverture (~16 reads Firestore en parallèle,
 * <500 ms typique) et le sauvegarde au click "Télécharger" avant de
 * lancer la génération PDF.
 */

import {
  doc,
  getDoc,
  writeBatch,
  type WriteBatch,
} from "firebase/firestore";
import { auth, db } from "../lib/firebase";

// ─── Types ───────────────────────────────────────────────────────────────

export type FicheSectionKey = "bien" | "reglement" | "risques" | "dvf";
export type EnvBlockKey =
  | "abf"
  | "bruit"
  | "ecoles"
  | "commerces"
  | "transports"
  | "arcep"
  | "dpe"
  | "filosofi"
  | "loyers"
  | "priceHistory";
export type AiBlockKey =
  | "synthese"
  | "redFlags"
  | "estimation"
  | "potentiel"
  | "visuel"
  | "priceTrend";

export type PdfIncludeState = {
  fiche: Record<FicheSectionKey, boolean>;
  env: Record<EnvBlockKey, boolean>;
  ai: Record<AiBlockKey, boolean>;
  // Mode "client-facing" : masque toute référence à l'IA dans le PDF.
  // - "Analyse IA" devient "Analyse" sur la page de section et en overline
  // - Les méta "Générée le … · modèle claude-…" sont retirées
  // L'agent active ça quand il envoie le PDF au client final, qui ne
  // doit pas forcément savoir qu'une analyse IA a été utilisée.
  // Stocké sur le doc fiche racine dans `pdfClientMode: boolean` (default
  // false = mode agent, avec transparence sur les sources IA).
  clientMode: boolean;
};

// ─── Constants ──────────────────────────────────────────────────────────

// Noms des docs Firestore (peuvent différer de la clé sémantique).
// Ex. ai/redflags (tout minuscule) vs. clé sémantique "redFlags" camelCase.
const AI_DOC_IDS: Record<AiBlockKey, string> = {
  synthese: "synthese",
  redFlags: "redflags", // NB : minuscule côté Firestore
  estimation: "estimation",
  potentiel: "potentiel",
  visuel: "visuel",
  priceTrend: "priceTrend",
};

const ENV_DOC_IDS: Record<EnvBlockKey, string> = {
  abf: "abf",
  bruit: "bruit",
  ecoles: "ecoles",
  commerces: "commerces",
  transports: "transports",
  arcep: "arcep",
  dpe: "dpe",
  filosofi: "filosofi",
  loyers: "loyers",
  priceHistory: "priceHistory",
};

// ─── Default = tout inclus ───────────────────────────────────────────────

export function defaultPdfIncludeState(): PdfIncludeState {
  return {
    fiche: {
      bien: true,
      reglement: true,
      risques: true,
      dvf: true,
    },
    env: {
      abf: true,
      bruit: true,
      ecoles: true,
      commerces: true,
      transports: true,
      arcep: true,
      dpe: true,
      filosofi: true,
      loyers: true,
      priceHistory: true,
    },
    ai: {
      synthese: true,
      redFlags: true,
      estimation: true,
      potentiel: true,
      visuel: true,
      priceTrend: true,
    },
    // Default : on affiche la section "Analyse IA" en mode agent (avec
    // les méta de génération). L'agent active explicitement clientMode
    // avant d'envoyer au client final.
    clientMode: false,
  };
}

// ─── Load state from Firestore ───────────────────────────────────────────

/**
 * Lit l'état courant des flags pdfInclude. Tout doc manquant ou tout
 * flag absent est traité comme `true` (opt-out design : par défaut tout
 * est inclus, l'agent décoche ce qu'il ne veut pas).
 */
export async function loadPdfIncludeState(
  ficheId: string,
): Promise<PdfIncludeState> {
  const user = auth.currentUser;
  if (!user) return defaultPdfIncludeState();

  const uid = user.uid;
  const state = defaultPdfIncludeState();

  // Fiche root doc — pour pdfIncludeFiche
  const fichePromise = getDoc(doc(db, "users", uid, "fiches", ficheId));

  // 10 env + 6 ai = 16 reads en parallèle
  const envPromises = (Object.keys(ENV_DOC_IDS) as EnvBlockKey[]).map(
    async (key) => {
      const d = await getDoc(
        doc(db, "users", uid, "fiches", ficheId, "env", ENV_DOC_IDS[key]),
      );
      return { key, data: d.exists() ? d.data() : null };
    },
  );
  const aiPromises = (Object.keys(AI_DOC_IDS) as AiBlockKey[]).map(
    async (key) => {
      const d = await getDoc(
        doc(db, "users", uid, "fiches", ficheId, "ai", AI_DOC_IDS[key]),
      );
      return { key, data: d.exists() ? d.data() : null };
    },
  );

  const [ficheSnap, envResults, aiResults] = await Promise.all([
    fichePromise,
    Promise.all(envPromises),
    Promise.all(aiPromises),
  ]);

  // Fiche sous-sections + clientMode
  if (ficheSnap.exists()) {
    const f = ficheSnap.data();
    const stored = f.pdfIncludeFiche as
      | Partial<Record<FicheSectionKey, boolean>>
      | undefined;
    if (stored) {
      for (const k of ["bien", "reglement", "risques", "dvf"] as const) {
        if (typeof stored[k] === "boolean") state.fiche[k] = stored[k]!;
      }
    }
    if (typeof f.pdfClientMode === "boolean") {
      state.clientMode = f.pdfClientMode;
    }
  }

  // Env blocks
  for (const { key, data } of envResults) {
    if (data && typeof data.pdfInclude === "boolean") {
      state.env[key] = data.pdfInclude;
    }
  }

  // AI blocks
  for (const { key, data } of aiResults) {
    if (data && typeof data.pdfInclude === "boolean") {
      state.ai[key] = data.pdfInclude;
    }
  }

  return state;
}

// ─── Save state to Firestore (batch) ─────────────────────────────────────

/**
 * Persiste l'état complet dans Firestore avec un seul `writeBatch`
 * (atomique, coût d'écriture identique à 1 round-trip réseau quel que
 * soit le nombre de docs).
 *
 * Important : on ne touche qu'aux docs qui existent déjà. Créer un doc
 * env/ai juste pour poser un flag `pdfInclude` serait :
 * 1. Rejeté par les règles Firestore sur /ai/* (create bloqué pour le
 *    client — les AI blocks n'existent qu'après passage par le CF
 *    generateAiX, pour éviter le spoofing).
 * 2. Incohérent sémantiquement sur /env/* : on aurait un doc
 *    "pdfInclude: true" sans data, qui risquerait de faire planter
 *    d'autres consommateurs attendant un champ `data`.
 *
 * On fait donc un pré-check getDoc sur les 16 blocks env+ai (~500 ms)
 * avant de constituer le batch.
 */
export async function savePdfIncludeState(
  ficheId: string,
  state: PdfIncludeState,
): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");

  const uid = user.uid;

  // Pré-check existence des 16 blocks en parallèle.
  const envKeys = Object.keys(ENV_DOC_IDS) as EnvBlockKey[];
  const aiKeys = Object.keys(AI_DOC_IDS) as AiBlockKey[];

  const [envExists, aiExists] = await Promise.all([
    Promise.all(
      envKeys.map(async (key) => {
        const d = await getDoc(
          doc(db, "users", uid, "fiches", ficheId, "env", ENV_DOC_IDS[key]),
        );
        return { key, exists: d.exists() };
      }),
    ),
    Promise.all(
      aiKeys.map(async (key) => {
        const d = await getDoc(
          doc(db, "users", uid, "fiches", ficheId, "ai", AI_DOC_IDS[key]),
        );
        return { key, exists: d.exists() };
      }),
    ),
  ]);

  const batch: WriteBatch = writeBatch(db);

  // Fiche root — merge pour ne pas écraser les autres champs. Le doc
  // fiche existe toujours (prérequis pour afficher la modale).
  batch.set(
    doc(db, "users", uid, "fiches", ficheId),
    {
      pdfIncludeFiche: state.fiche,
      pdfClientMode: state.clientMode,
      updatedAt: new Date(),
    },
    { merge: true },
  );

  // Env blocks — skip les docs inexistants (toggle sans effet).
  for (const { key, exists } of envExists) {
    if (!exists) continue;
    batch.set(
      doc(db, "users", uid, "fiches", ficheId, "env", ENV_DOC_IDS[key]),
      { pdfInclude: state.env[key] },
      { merge: true },
    );
  }

  // AI blocks — idem + règles Firestore exigent un UPDATE pur (pas de
  // create) et des affectedKeys ⊆ { pdfInclude }. `set(..., merge: true)`
  // sur un doc existant = update, les autres champs restent intacts.
  for (const { key, exists } of aiExists) {
    if (!exists) continue;
    batch.set(
      doc(db, "users", uid, "fiches", ficheId, "ai", AI_DOC_IDS[key]),
      { pdfInclude: state.ai[key] },
      { merge: true },
    );
  }

  await batch.commit();
}
